import { Dispatch, useCallback, useContext, useState } from 'react';

import i18n from '@nextroll/ar-i18n';
import { Alerts } from '@nextroll/ar-style-base';

import useAbortController from 'hooks/useAbortController';

import { isAbortReasonComponentUnmount, isErrorGenericAborted } from 'util/errors';

import { NotificationContext } from './useNotification';

const DEFAULT_ERROR_NOTIFICATION_TITLE = 'Workflows is experiencing some technical issues at the moment';
const DEFAULT_ERROR_NOTIFICATION_MESSAGE = 'Please try again later.';

export interface RequestError {
    message: string;
    parsedBody: any;
    requestData: any;
    requestMethod: string;
    response: Response;
}

export interface IHandlerParams {
    notificationOptions?: {
        successNotificationTitle?: string;
        successNotificationMessage?: string;
        errorNotificationTitle?: string;
        errorNotificationMessage?: string;
    };
    errorHandler?: (error: RequestError) => boolean;
}

const useRequest = <T, >() => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined as any);
    const getSignal = useAbortController();

    const { addAlert } = useContext(NotificationContext);

    const parseError = (requestError: RequestError) => {
        if (requestError?.message) {
            return requestError;
        }
        const message = typeof requestError === 'string' ? requestError : JSON.stringify(requestError);
        if (`${requestError.constructor}`.includes('Object')) {
            return { ...requestError, message };
        }
        return { message };
    };

    /**
     * Wrap request calls in this method and it will handle the following things automatically:
     * - notifiction
     * - loading state
     * - error when available.
     * - You can also pass as a prop an error handler to handle errors differently, and return a boolean
     * - to decide whether or not to show the error notification
     * e.g.
     * const response = await call(async () => await getWorkflowList(advertisableEid))
     */
    const call = useCallback(async (request: () => Promise<T>, handlerParams?: IHandlerParams) => {
        setLoading(true);
        const abortSignal = getSignal();

        try {
            const result = await request();
            if (abortSignal.aborted) {
                return;
            }
            handlerParams?.notificationOptions?.successNotificationTitle && addAlert?.({
                type: Alerts.ALERT_TYPE_SUCCESS,
                title: handlerParams.notificationOptions?.successNotificationTitle,
                message: handlerParams.notificationOptions?.successNotificationMessage,
            });
            setLoading(false);
            return result;
        } catch (requestError: any) {
            // tests can seemingly have an aborted signal with no reason (which shouldnt happen)
            if (abortSignal.aborted && (isAbortReasonComponentUnmount(abortSignal.reason) || !abortSignal.reason)) {
                return;
            }
            setLoading(false);
            if (isErrorGenericAborted(requestError)) {
                // this isn't really an error, so swallow it
                return;
            }
            setLoading(false);
            setError(requestError?.parsedBody?.error || parseError(requestError));
            const errorHandled = handlerParams?.errorHandler
                ? handlerParams.errorHandler(requestError)
                : false;

            if (errorHandled) {
                return;
            }

            addAlert?.({
                type: Alerts.ALERT_TYPE_ERROR,
                title: handlerParams?.notificationOptions?.errorNotificationTitle
                || i18n.gettext(DEFAULT_ERROR_NOTIFICATION_TITLE),

                message: handlerParams?.notificationOptions?.errorNotificationMessage
                || i18n.gettext(DEFAULT_ERROR_NOTIFICATION_MESSAGE),
            });
            console.error('UI Error:', requestError.message);
        }
    }, [addAlert, getSignal]);


    return [call, loading, error, setError] as [
        typeof call,
        boolean,
        { message: string } | undefined,
        Dispatch<any>,
    ];
};

export default useRequest;
export type UseRequest = ReturnType<typeof useRequest>;
